import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { formatarMoedaBRL } from '../../../core/utils/moeda.util';
import { formatarCnpj, formatarTelefone, montarEnderecoEmpresa } from '../../../core/utils/empresa.util';
import { CupomVendaBalcao } from '../venda-balcao-cupom-modal/venda-balcao-cupom-modal';
import { TIPO_VENDA_A_VISTA, TIPO_VENDA_MISTA, TIPO_VENDA_PARCELADA } from '../../../models/venda-v2.model';

export const CUPOM_LABELS_FORMA: Record<string, string> = {
  'CREDITO A VISTA': 'Crédito à Vista',
  'CREDITO PARCELADO': 'Crédito Parcelado',
  'DEBITO': 'Débito',
  'ESPECIE': 'Espécie',
  'PIX': 'Pix',
  'BOLETO': 'Boleto',
  'CHEQUE': 'Cheque',
  'VENDA MISTA': 'Venda Mista'
};

export const CUPOM_LABELS_TIPO_VENDA: Record<string, string> = {
  [TIPO_VENDA_A_VISTA]: 'À Vista',
  [TIPO_VENDA_PARCELADA]: 'Parcelada',
  [TIPO_VENDA_MISTA]: 'Venda Mista'
};

/** Cupom não fiscal (80mm) reutilizável: mesmo layout do cupom da venda balcão, sem observação. */
@Component({
  selector: 'app-cupom-venda-modal',
  standalone: true,
  imports: [CurrencyPipe],
  templateUrl: './cupom-venda-modal.html',
  styleUrl: './cupom-venda-modal.css'
})
export class CupomVendaModal {
  @Input() isOpen = false;
  @Input() cupom: CupomVendaBalcao | null = null;
  @Output() closeModal = new EventEmitter<void>();

  private readonly toastr = inject(ToastrService);

  readonly formatarCnpj = formatarCnpj;
  readonly formatarTelefone = formatarTelefone;
  readonly montarEnderecoEmpresa = montarEnderecoEmpresa;

  sair(): void {
    this.closeModal.emit();
  }

  formatarData(iso: string): string {
    const [ano, mes, dia] = iso.split('-');
    return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
  }

  formatarDataHora(d: Date): string {
    return d.toLocaleString('pt-BR');
  }

  imprimir(): void {
    const c = this.cupom;
    if (!c) return;

    const esc = (v: unknown): string =>
      String(v ?? '').replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
    const linha = (rotulo: string, valor: string) =>
      `<div class="linha"><span>${esc(rotulo)}</span><span>${esc(valor)}</span></div>`;

    const emp = c.empresa;
    const empLinha = (v: string | null | undefined, extra = ''): string =>
      v && v.trim() ? `<div${extra ? ` class="${extra}"` : ''}>${esc(v)}</div>` : '';
    const empresaHtml = emp ? `<div class="empresa centro">
    ${empLinha(emp.nomeFantasia, 'empresa-fantasia')}
    ${empLinha(emp.razaoSocial)}
    ${empLinha(emp.cnpj?.trim() ? 'CNPJ ' + formatarCnpj(emp.cnpj) : '')}
    ${empLinha(emp.inscricaoEstadual?.trim() ? 'IE ' + emp.inscricaoEstadual.trim() : '')}
    ${empLinha(montarEnderecoEmpresa(emp))}
    ${empLinha(emp.telefone ? formatarTelefone(emp.telefone) : '')}
    ${empLinha(emp.email)}
  </div>` : '';

    const pagamento = [
      linha('Tipo', c.tipoVenda),
      linha('Forma', c.formaPagamento),
      c.bandeira ? linha('Bandeira', c.bandeira) : '',
      c.numParcelas > 1 ? linha('Parcelas', `${c.numParcelas}x`) : '',
      c.valorEntrada ? linha('Entrada', formatarMoedaBRL(c.valorEntrada)) : '',
      c.previsaoEntrega ? linha('Previsão entrega', this.formatarData(c.previsaoEntrega)) : ''
    ].join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>Cupom não fiscal #${esc(c.numeroReferencia)}</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 12px; width: 72mm; margin: 0 auto; color: #000; }
  .centro { text-align: center; }
  .aviso { border: 2px solid #000; padding: 4px; margin: 6px 0; font-weight: bold; text-align: center; }
  .sep { border-top: 1px dashed #000; margin: 6px 0; }
  .linha { display: flex; justify-content: space-between; gap: 8px; }
  .total { font-size: 15px; font-weight: bold; }
  .empresa { margin: 6px 0; word-break: break-word; overflow-wrap: anywhere; }
  .empresa-fantasia { font-weight: bold; font-size: 14px; }
  img { max-width: 40mm; }
</style></head><body>
  <div class="centro"><img src="${window.location.origin}/assets/logo/logo_transparente.png" alt=""></div>
  ${empresaHtml}
  <div class="aviso">DOCUMENTO NÃO FISCAL<br>SEM VALOR FISCAL</div>
  <div class="centro">Comprovante de Venda</div>
  <div class="sep"></div>
  ${linha('Referência', '#' + c.numeroReferencia)}
  ${linha('Emissão', this.formatarDataHora(c.emitidoEm))}
  ${c.nomeVendedor ? linha('Vendedor', c.nomeVendedor) : ''}
  <div class="sep"></div>
  <div>Cliente: ${esc(c.nomeCliente)}</div>
  ${c.cpfCnpjCliente ? `<div>CPF/CNPJ: ${esc(c.cpfCnpjCliente)}</div>` : ''}
  <div class="sep"></div>
  ${linha('Subtotal', formatarMoedaBRL(c.subtotal))}
  ${linha('Desconto', '- ' + formatarMoedaBRL(c.desconto))}
  <div class="linha total"><span>TOTAL</span><span>${esc(formatarMoedaBRL(c.valorTotal))}</span></div>
  <div class="sep"></div>
  ${pagamento}
  <div class="sep"></div>
  <div class="aviso">ESTE DOCUMENTO NÃO É UM CUPOM FISCAL</div>
</body></html>`;

    const janela = window.open('', '_blank');
    if (!janela) {
      this.toastr.warning('Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.');
      return;
    }
    janela.document.write(html);
    janela.document.close();
    janela.focus();
    janela.onload = () => janela.print();
    setTimeout(() => janela.print(), 500);
  }
}
